import { useState, useEffect, useRef } from "react";
import { generateToken } from "@/components/main/utils/tokens/TokensApi";
import { TokenList } from "@/components/main/utils/tokens/TokenList";
import { TokenDialog } from "@/components/main/utils/tokens/TokenDialog";
import { AccessConnectPanel } from "@/components/main/utils/tokens/AccessConnectPanel";
import { OrgBadge } from "@/components/main/utils/tokens/OrgBadge";
import {
  normalizeName,
  tokenInOrg,
} from "@/components/main/utils/tokens/utils";
import { useTokensStore } from "@/components/main/stores/tokensStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { useAppInitializationStore } from "@/components/main/stores/appInitializationStore";
import { useShallow } from "zustand/react/shallow";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { ColumnTopSpacer } from "@/components/main/pages/pageColumn";

export const AccessPage = () => {
  const activeOrgId = useOrgContextStore((s) => s.activeOrgId);
  const activeOrg = useOrgContextStore((s) =>
    s.orgs.find((o) => o.org_id === s.activeOrgId),
  );
  const {
    tokens,
    fetchTokensList,
    newToken,
    openDialog,
    resetDialog,
    setGeneratedToken,
    setIsGeneratingToken,
  } = useTokensStore(
    useShallow((state) => ({
      tokens: state.tokens,
      fetchTokensList: state.fetchTokensList,
      newToken: state.newToken,
      openDialog: state.openDialog,
      resetDialog: state.resetDialog,
      setGeneratedToken: state.setGeneratedToken,
      setIsGeneratingToken: state.setIsGeneratingToken,
    })),
  );
  const { isAppInitialized, tokensInitialized } = useAppInitializationStore(
    useShallow((state) => ({
      isAppInitialized: state.isAppInitialized(),
      tokensInitialized: state.tokensInitialized,
    })),
  );
  const initializedRef = useRef(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      void fetchTokensList({
        showLoading: !isAppInitialized && !tokensInitialized,
        refresh: tokensInitialized,
      });
    }
  }, [fetchTokensList, tokensInitialized, isAppInitialized]);

  const refresh = () => fetchTokensList({ showLoading: false, refresh: true });

  const handleGenerateToken = async () => {
    setIsGeneratingToken(true);
    try {
      if (!newToken.description.trim()) {
        throw new Error("Token description is required");
      }

      if (!activeOrgId) {
        throw new Error("No active organization to bind the token to");
      }
      const result = await generateToken(
        newToken.expiration_time,
        newToken.description,
        newToken.scope,
        activeOrgId,
      );

      setGeneratedToken(result.token);
    } catch (error) {
      showNotificationGlobal(
        (error as Error).message || "Failed to generate token",
        "error",
      );
    } finally {
      setIsGeneratingToken(false);
    }
  };

  const handleTokenDialogDone = async () => {
    const created = useTokensStore.getState().generatedToken !== null;
    const name = normalizeName(newToken.description);
    resetDialog();
    const list = await refresh();
    if (created) {
      showNotificationGlobal("Token created", "success");
      setHighlightId(
        list.find(
          (t) =>
            t.get_status === "active" &&
            normalizeName(t.get_description) === name,
        )?.get_token_id ?? null,
      );
    }
  };

  return (
    <>
      <div className="relative flex flex-col items-center w-full h-full bg-bg overflow-y-auto [scrollbar-gutter:stable_both-edges] px-6">
        <ColumnTopSpacer />
        <div
          className="w-full select-none flex flex-col gap-8"
          style={{
            maxWidth: "45rem",
            paddingBottom: "2.5rem",
          }}
        >
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-fg-title leading-none">Access</h1>
            <OrgBadge />
          </div>

          <AccessConnectPanel />

          {tokensInitialized && (
            <TokenList
              tokens={tokens.filter((t) => tokenInOrg(t, activeOrg))}
              onGenerate={openDialog}
              onChanged={refresh}
              highlightId={highlightId}
            />
          )}
        </div>
      </div>

      <TokenDialog
        onGenerate={handleGenerateToken}
        onDone={handleTokenDialogDone}
      />
    </>
  );
};
